import { ReactNode, useEffect, useState } from 'react';
import { ChatFileIcon } from '../../../assets/svg';
import { ConversationAttachmentEntity } from '../../../types/conversation.types';
import { formatBytes } from '../helpers/chatFormat';
import { ChatLightbox } from './ChatLightbox';
import { ChatVideoPlayer } from './ChatVideoPlayer';
import { ChatVoicePlayer } from './ChatVoicePlayer';
import {
  AlbumTile,
  AudioPill,
  FileChip,
  FileName,
  FileSize,
  MediaAlbum,
  MediaFrame,
  MediaLoader,
  MediaSkeleton,
} from './MessageAttachments.styled';

/** The tallest a picture is drawn - `MediaFrame`'s own `max-height`. */
const MAX_IMAGE_HEIGHT = 300;
/**
 * What to reserve when the attachment carries no dimensions - a heic the
 * browser cannot measure, or anything uploaded before the client started
 * measuring at all.
 *
 * As TALL as the CSS will ever draw it, deliberately. A guess that is too short
 * makes the message grow when the picture lands, which pushes the whole
 * conversation down under the reader; too tall only shrinks it, and at the
 * bottom of a thread the browser just clamps the scroll. One is a bug, the
 * other is invisible. Four unmeasured pictures at the old 180 were 480px the
 * thread grew AFTER opening, which is exactly how far from the end it stopped.
 */
const FALLBACK_BOX = { width: 240, height: MAX_IMAGE_HEIGHT };

/**
 * The box a picture will occupy once it has loaded, worked out before it does.
 *
 * The dimensions come from the attachment - the client measures them at upload
 * - so the space can be reserved rather than discovered.
 *
 * This has to mirror `MediaFrame`'s CSS exactly or the picture jumps as it
 * lands. That is height alone: the image is capped at 300 tall and otherwise
 * drawn at its natural size, so a wide screenshot legitimately comes out ~530
 * across. Capping the width here too (an earlier version borrowed the album's
 * 320) reserved a box narrower than the picture and moved everything when it
 * arrived. `max-width: 100%` in the style below is what keeps it inside the
 * column, exactly as the CSS does after load.
 */
const reservedBox = (attachment: ConversationAttachmentEntity) => {
  const { width, height } = attachment;
  if (!width || !height) return FALLBACK_BOX;

  const scale = Math.min(1, MAX_IMAGE_HEIGHT / height);
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
};

/**
 * One picture, holding its own place while it downloads.
 *
 * Without this the frame is zero-sized until the image decodes, which in a
 * captioned message drags the whole bubble down to a sliver - the caption is
 * sized from the picture, not the other way round.
 */
const ChatImage = ({
  attachment,
  flush,
  uploading,
  onClick,
  children,
}: {
  attachment: ConversationAttachmentEntity;
  flush?: boolean;
  /** The message is on screen but has not reached the server yet. */
  uploading?: boolean;
  onClick?: () => void;
  /** The timestamp chip, when this is the last thing in the message. */
  children?: ReactNode;
}) => {
  const { url } = attachment;
  const [loaded, setLoaded] = useState(false);
  /**
   * Whether a picture has ever been on screen here.
   *
   * A sent photo is drawn twice: first from the file on this machine, then
   * again from the server's copy once the message comes back. That second
   * source has to reserve its space like the first (an `img` mid-swap has no
   * intrinsic size to lay out from, and the caption is sized from the picture)
   * - but it must NOT bring the skeleton back, which would drop a grey panel
   * over a photograph the user is already looking at.
   */
  const [shown, setShown] = useState(false);

  const box = reservedBox(attachment);

  // Back to reserving the box whenever the source changes under us.
  useEffect(() => setLoaded(false), [url]);

  /*
   * A pending message points at the file still on this machine. Once the
   * server's row replaces it that URL has no owner left, and an object URL
   * holds the whole file in memory until it is released - so release it here,
   * where its last use is.
   */
  useEffect(() => {
    if (!url.startsWith('blob:')) return undefined;
    return () => URL.revokeObjectURL(url);
  }, [url]);

  return (
    <MediaFrame
      $flush={flush}
      $clickable={!!onClick}
      onClick={onClick}
      /* Dropped once the picture is in: from then on it sizes itself, and a
         pinned width would letterbox anything whose real ratio differs from
         what the attachment claimed. */
      style={loaded ? undefined : { width: box.width, height: box.height }}
    >
      <img
        src={url}
        alt={attachment.name || 'Attachment'}
        onLoad={() => {
          setLoaded(true);
          setShown(true);
        }}
        /* A broken image must not leave the skeleton shimmering forever. */
        onError={() => {
          setLoaded(true);
          setShown(true);
        }}
      />
      {!loaded && !shown && <MediaSkeleton />}
      {uploading && <MediaLoader />}
      {children}
    </MediaFrame>
  );
};

/**
 * Images and videos: the visual half of a message, rendered bare or - when the
 * message has a caption - flush inside the captioned group.
 *
 * Pictures are grouped: one is shown at its own size, several become an album,
 * and either way the whole message opens as one gallery. Videos keep their own
 * full-width frame - a player cropped into a square tile is not a player.
 */
export const MediaAttachments = ({
  attachments,
  flush,
  uploading,
  meta,
}: {
  attachments: ConversationAttachmentEntity[];
  /** Inside a captioned group, so the group's radius does the rounding. */
  flush?: boolean;
  /** The message is still on its way to the server. */
  uploading?: boolean;
  /** Timestamp chip, laid over the last item when no caption carries it. */
  meta?: ReactNode;
}) => {
  const images = attachments.filter((item) => item.type === 'image');
  const videos = attachments.filter((item) => item.type !== 'image');

  /** Which picture the gallery is showing; null while it is closed. */
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // The chip belongs to whatever renders last, and videos go after the album.
  const metaOnVideos = videos.length > 0;

  return (
    <>
      {images.length === 1 && (
        <ChatImage
          attachment={images[0]}
          flush={flush}
          uploading={uploading}
          onClick={() => setOpenIndex(0)}
        >
          {!metaOnVideos && meta}
        </ChatImage>
      )}

      {images.length > 1 && (
        <MediaAlbum $flush={flush}>
          {images.map((image, index) => (
            <AlbumTile
              key={image.id}
              /* An odd count leads with a wide tile, so the last row is full. */
              $wide={index === 0 && images.length % 2 === 1}
              onClick={() => setOpenIndex(index)}
            >
              <img src={image.url} alt={image.name || 'Attachment'} />
              {!metaOnVideos && index === images.length - 1 && meta}
            </AlbumTile>
          ))}
        </MediaAlbum>
      )}

      {videos.map((video, index) => (
        <MediaFrame key={video.id} $flush={flush}>
          <ChatVideoPlayer attachment={video} flush={flush} />
          {index === videos.length - 1 && meta}
        </MediaFrame>
      ))}

      <ChatLightbox
        images={images}
        index={openIndex}
        onIndex={setOpenIndex}
        onClose={() => setOpenIndex(null)}
      />
    </>
  );
};

/**
 * Voice notes and documents. These stay outside any bubble or group - the pill
 * and the chip are their own surface - and the timestamp rides inside them.
 */
export const PlainAttachments = ({
  attachments,
  meta,
}: {
  attachments: ConversationAttachmentEntity[];
  meta?: ReactNode;
}) => (
  <>
    {attachments.map((attachment, index) => {
      const trailing = index === attachments.length - 1 ? meta : null;

      return attachment.type === 'audio' ? (
        <AudioPill key={attachment.id}>
          <ChatVoicePlayer attachment={attachment} />
          {trailing}
        </AudioPill>
      ) : (
        <FileChip
          key={attachment.id}
          href={attachment.url}
          target="_blank"
          rel="noreferrer"
          download={attachment.name || true}
        >
          <ChatFileIcon />
          <FileName>{attachment.name || 'Attachment'}</FileName>
          <FileSize>{formatBytes(attachment.size)}</FileSize>
          {trailing}
        </FileChip>
      );
    })}
  </>
);
